import React, { useState, useEffect } from "react";
import axios from "axios";

const { rest_nonce, pro_active, license_active } = window.INSTANT_PAGE_LOAD;

const defaultSettings = {
	enable_plugin: true,
	preload_strategy: "viewport",
	debounce_interval: 100,
	enable_cache: false,
	cache_expiry: 20,
	cache_size_limit: 8, // Default cache size limit (interpreted as number of entries)
	preload_priority_urls: "", // Default priority URLs
	enable_loading_icon: true,
	loading_animation: "spinner",
	exclude_urls: "",
	hover_delay: 0,
	enable_woocommerce: false,
	enable_exclude_admin_urls: true,
	cache_limit_policy: "limit_entries",
  };
  
  const Settings = () => {
	const [settings, setSettings] = useState({});
	const [isSaving, setIsSaving] = useState(false);
	const [savingStatus, setSavingStatus] = useState("");
  
	const apiUrl = "/wp-json/instant-page-load/v1/settings/";
  
	const handleInputChange = (key, value) => {
	  setSettings((prev) => ({
		...prev,
		[key]: value,
	  }));
	};
  
	const handleSave = (e) => {
	  e.preventDefault();
  
	  setIsSaving(true);
	  setSavingStatus("saving");
  
	  // Optionally trigger clear cache flag
	  setLocalStorageFlagWithRetry("clearCache", "true", 3);
  
	  axios
		.post(
		  apiUrl,
		  { settings },
		  {
			headers: {
			  "Content-Type": "application/json",
			  "X-WP-NONCE": rest_nonce,
			},
		  }
		)
		.then(() => {
		  setSavingStatus("saved");
		  setIsSaving(false);
		  setTimeout(() => setSavingStatus(""), 1000);
		})
		.catch(() => {
		  setSavingStatus("error");
		  setIsSaving(false);
		});
	};
  
	useEffect(() => {
	  const fetchSettings = async () => {
		try {
		  const response = await axios.get(apiUrl, {
			headers: {
			  "X-WP-NONCE": rest_nonce,
			},
		  });
  
		  const fetchedSettings =
			response.data?.settings?.settings || defaultSettings;
  
		  setSettings(fetchedSettings);
		} catch (error) {
		  console.error("Error fetching settings:", error);
		  setSettings(defaultSettings);
		}
	  };
  
	  fetchSettings();
	}, []);
  
	const handleReset = () => {
	  if (!window.confirm("Are you sure you want to reset all data?")) {
		return;
	  }
  
	  // Set settings to defaultSettings object
	  setSettings(defaultSettings);
  
	  setIsSaving(true);
	  setSavingStatus("saving");
  
	  axios
		.post(
		  apiUrl,
		  { settings: defaultSettings },
		  {
			headers: {
			  "Content-Type": "application/json",
			  "X-WP-NONCE": rest_nonce,
			},
		  }
		)
		.then(() => {
		  setSavingStatus("saved");
		  setIsSaving(false);
		  setTimeout(() => setSavingStatus(""), 1000);
		})
		.catch(() => {
		  setSavingStatus("error");
		  setIsSaving(false);
		});
	};
  
	function setLocalStorageFlagWithRetry(key, value, retries) {
	  let attempt = 0;
	  const interval = setInterval(() => {
		try {
		  localStorage.setItem(key, value);
		  console.log(`LocalStorage set successfully: ${key} = ${value}`);
		  clearInterval(interval);
		} catch (error) {
		  console.warn(`Attempt ${attempt + 1} to set localStorage failed.`, error);
		  if (++attempt >= retries) {
			clearInterval(interval);
			console.error("Failed to set localStorage after multiple attempts.");
		  }
		}
	  }, 100);
	}

// const pro_active = license_active;

  return (
    <div className="settings-container bg-gray-50 min-h-screen p-6">
      {/* Header */}
      <div className="flex mb-6 bg-white px-6 py-4 rounded justify-between items-center shadow">
        <div>
          <h2 className="font-semibold text-lg text-blue-950">
            Instant Page Load - Plugin Settings
          </h2>
          <p className="text-blue-950">
            Customize the settings for preloading and page transitions below.
          </p>
        </div>
        <div className="flex gap-4">
          <button
            className={`px-6 py-2 rounded ${
              JSON.stringify(settings) === JSON.stringify()
                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                : "bg-blue-50 text-blue-500 hover:bg-blue-500 hover:text-white"
            }`}
            onClick={handleReset}
            disabled={
              JSON.stringify(settings) === JSON.stringify()
            }
          >
            Reset
          </button>
          <button
            className={`px-6 py-2 rounded ${
              isSaving
                ? "bg-gray-300 text-gray-600"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Settings Form */}
		<div className="columns-1 md:columns-2 gap-6 space-y-6">
		{/* General Settings */}
		<div className="bg-white p-4 rounded shadow break-inside-avoid">
			<h3 className="text-lg font-medium text-blue-500 mb-4">General Settings</h3>
			
			<div className="mb-4">
			<label className="block text-gray-700 font-medium mb-2">Enable Plugin:</label>
			<label className="relative inline-flex items-center cursor-pointer">
				<input
				type="checkbox"
				checked={settings.enable_plugin || false}
				onChange={(e) => handleInputChange("enable_plugin", e.target.checked)}
				className="sr-only"
				/>
				<div className={`w-10 h-6 rounded-full transition-colors duration-200 ${
				settings.enable_plugin ? 'bg-blue-500' : 'bg-gray-300'
				}`}>
				<div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform duration-200 ${
					settings.enable_plugin ? 'translate-x-4' : ''
				}`}></div>
				</div>
			</label>
			</div>
			<div className="mb-4">
				<label className="block text-gray-700 font-medium mb-2">Exclude All Admin URLs:</label> 
				<label className="relative inline-flex items-center cursor-pointer">
					<input
						type="checkbox"
						checked={settings.enable_exclude_admin_urls || false}
						onChange={(e) => handleInputChange("enable_exclude_admin_urls", e.target.checked)}
						className="sr-only"
					/>
					<div className={`w-10 h-6 rounded-full transition-colors duration-200 ${
						settings.enable_exclude_admin_urls ? 'bg-blue-500' : 'bg-gray-300'
					}`}>
						<div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform duration-200 ${
							settings.enable_exclude_admin_urls ? 'translate-x-4' : ''
						}`}></div>
					</div>
				</label>
			</div>

			<div className="mb-4">
			<label className="block text-gray-700 font-medium mb-2">Preload Strategy:</label>
			<select
				value={settings.preload_strategy || "viewport"}
				onChange={(e) => handleInputChange("preload_strategy", e.target.value)}
				className="w-full px-4 py-2 border rounded-md"
			>
				<option value="viewport">Links in Viewport</option>
				<option value="all">All Links on Page</option>
				<option value="hover">Hovered Links</option>
			</select>
			</div>

			<div className="mb-4">
			<label className="block text-gray-700 font-medium mb-2">Debounce Interval (ms):</label>
			<input
				type="number"
				value={settings.debounce_interval || 100}
				onChange={(e) => handleInputChange("debounce_interval", e.target.value)}
				className="w-full px-4 py-2 border rounded-md"
			/>
			</div>
		</div>

		{/* Cache Settings (Pro Feature) */}
		<div className="bg-white p-4 rounded shadow relative break-inside-avoid">
			<h3 className="text-lg font-medium text-blue-500 mb-4">
			Cache Settings
			{!pro_active && (
				<a 
					href="https://worzen.com/instant-page-load/#pricing" 
					target="_blank" 
					rel="noopener noreferrer"
					>
					<span className="text-xs bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 ml-2 rounded-full shadow-lg border border-purple-500 animate-pulse">
						🚀 PRO
					</span>
					
				</a>
			)}
			</h3>

			<div className="mb-4">
			<label className="block text-gray-700 font-medium mb-2">Enable Page Caching:</label>
			<label className="relative inline-flex items-center cursor-pointer">
				<input
				type="checkbox"
				disabled={!pro_active}
				checked={settings.enable_cache || false}
				onChange={(e) => handleInputChange("enable_cache", e.target.checked)}
				className="sr-only"
				/>
				<div className={`w-10 h-6 rounded-full transition-colors duration-200 ${
				settings.enable_cache ? 'bg-purple-500' : 'bg-gray-300'
				} ${!pro_active ? 'opacity-50 cursor-not-allowed' : ''}`}>
				<div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform duration-200 ${
					settings.enable_cache ? 'translate-x-4' : ''
				} ${!pro_active ? 'bg-gray-100' : ''}`}></div>
				</div>
			</label>
			</div>

			<div className="mb-4">
			<label className="block text-gray-700 font-medium mb-2">Cache Expiry Time (minutes):</label>
			<input
				type="number"
				disabled={!pro_active}
				value={settings.cache_expiry || 20}
				onChange={(e) => handleInputChange("cache_expiry", e.target.value)}
				className="w-full px-4 py-2 border rounded-md"
			/>
			</div>

			<div className="mb-4">
			<label className="block text-gray-700 font-medium mb-2">Cache Size Limit (MB):</label>
			<input
				type="number"
				disabled={!pro_active}
				value={settings.cache_size_limit || 8}
				onChange={(e) => handleInputChange("cache_size_limit", e.target.value)}
				className="w-full px-4 py-2 border rounded-md"
				max="10"
			/>
			</div>

			<button
			id="clear-cache-button"
			disabled={!pro_active}
			className={`${
				pro_active ? "bg-red-500 hover:bg-red-600" : "bg-gray-400 cursor-not-allowed"
			} text-white px-6 py-2 rounded`}
			onClick={() => {
				if (pro_active && window.confirm("Are you sure you want to clear the frontend cache?")) {
					setLocalStorageFlagWithRetry("clearCache", "true", 3);
					alert("Cache clear initiated for frontend.");
				}
			}}
			>
			Clear Cache
			</button>
		</div>

		{/* Loading Animation (Free Feature) */}
		<div className="bg-white p-4 rounded shadow relative break-inside-avoid">
			<h3 className="text-lg font-medium text-blue-500 mb-4">
				Loading Animation
			</h3>

			<div className="mb-4">
				<label className="block text-gray-700 font-medium mb-2">Enable Loading Animation:</label>
				<label className="relative inline-flex items-center cursor-pointer">
					<input
						type="checkbox"
						checked={settings.enable_loading_icon || false}
						onChange={(e) => handleInputChange("enable_loading_icon", e.target.checked)}
						className="sr-only"
					/>
					<div className={`w-10 h-6 rounded-full transition-colors duration-200 ${
						settings.enable_loading_icon ? 'bg-purple-500' : 'bg-gray-300'
					}`}>
						<div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform duration-200 ${
							settings.enable_loading_icon ? 'translate-x-4' : ''
						}`}></div>
					</div>
				</label>
			</div>

			<div className="mb-4">
				<label className="block text-gray-700 font-medium mb-2">Animation Type:</label>
				<select
					value={settings.loading_animation || "spinner"}
					onChange={(e) => handleInputChange("loading_animation", e.target.value)}
					className="w-full px-4 py-2 border rounded-md"
				>
					<option value="spinner">Spinner</option>
					<option value="progress_bar">Progress Bar</option>
					<option value="dots">Dots</option>
				</select>
			</div>
		</div>
		

		{/* Advanced Settings (Pro Feature) */}
		<div className="bg-white p-4 rounded shadow relative break-inside-avoid">
			<h3 className="text-lg font-medium text-blue-500 mb-4">
			Advanced Settings
			{!pro_active && (
				<a 
					href="https://worzen.com/instant-page-load/#pricing" 
					target="_blank" 
					rel="noopener noreferrer"
					>
					<span className="text-xs bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 ml-2 rounded-full shadow-lg border border-purple-500 animate-pulse">
					🚀 PRO
					</span>
				</a>
			)}
			</h3>

			<div className="mb-4">
			<label className="block text-gray-700 font-medium mb-2">Exclude URLs (comma-separated):</label>
			<textarea
				disabled={!pro_active}
				value={settings.exclude_urls || ""}
				onChange={(e) => handleInputChange("exclude_urls", e.target.value)}
				className="w-full px-4 py-2 border rounded-md"
			/>
			</div>

			<div className="mb-4">
			<label className="block text-gray-700 font-medium mb-2">Priority URLs (comma-separated):</label>
			<textarea
				disabled={!pro_active}
				value={settings.preload_priority_urls || ""}
				onChange={(e) => handleInputChange("preload_priority_urls", e.target.value)}
				className="w-full px-4 py-2 border rounded-md"
			/>
			</div>

			<div className="mb-4">
				<label className="block text-gray-700 font-medium mb-2">Preload Hover Delay (ms):</label>
				<input
					type="number"
					disabled={!pro_active}
					value={settings.hover_delay || 0}
					onChange={(e) => handleInputChange("hover_delay", e.target.value)}
					className="w-full px-4 py-2 border rounded-md"
				/>
			</div>
			<div className="mb-4">
				<label className="block text-gray-700 font-medium mb-2">
					Cache Limit Policy:
				</label>
				<select
					disabled={!pro_active}
					value={settings.cache_limit_policy || "limit_entries"}
					onChange={(e) => handleInputChange("cache_limit_policy", e.target.value)}
					className="w-full px-4 py-2 border rounded-md"
				>
					<option value="limit_entries">Stop adding new entries when the cache is full</option>
					<option value="auto_clear">Remove oldest entries when the cache exceeds the limit</option>
				</select>
			</div>

		</div>

		{/* Support Addons */}
		<div className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 rounded-xl shadow-lg border-2 border-purple-100 break-inside-avoid">
			<div className="flex items-center justify-between mb-4">
			<h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
				🚀 Power Integrations
			</h3>
			{!pro_active && (
				<a 
					href="https://worzen.com/instant-page-load/#pricing" 
					target="_blank" 
					rel="noopener noreferrer"
					>
					<span className="text-xs bg-gradient-to-r from-blue-500 to-purple-500 text-white px-3 py-1 rounded-full font-medium">
					🔒 PRO
					</span>
				</a>
			)}
			</div>

			<div className="space-y-3">
			<div className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
				<div>
				<label className="block font-semibold text-gray-800 mb-1">🛍️ WooCommerce Boost</label>
				<p className="text-sm text-gray-600">Accelerate product pages & checkout</p>
				</div>
				<label className="relative inline-flex items-center cursor-pointer">
					<input
						type="checkbox"
						disabled={!pro_active}
						checked={settings.enable_woocommerce || false}
						onChange={(e) => handleInputChange("enable_woocommerce", e.target.checked)}
						className="sr-only"
					/>
					<div className={`w-10 h-6 rounded-full transition-colors duration-200 ${
						settings.enable_woocommerce ? 'bg-purple-500' : 'bg-gray-300'
					} ${!pro_active ? 'opacity-50 cursor-not-allowed' : ''}`}>
						<div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform duration-200 ${
							settings.enable_woocommerce ? 'translate-x-4' : ''
						} ${!pro_active ? 'bg-gray-100' : ''}`}></div>
					</div>
				</label>

			</div>

			<div className="text-sm text-purple-700 border-t border-purple-100 pt-3">
				<div className="flex items-center mb-2">
				<span className="mr-2">✅</span>
				<span>Smart preloading for product variations</span>
				</div>
				<div className="flex items-center">
				<span className="mr-2">✅</span>
				<span>Instant cart updates optimization</span>
				</div>
			</div>
			</div>
		</div>
		</div>


      {/* Save Status Notification */}
      {savingStatus && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-md">
            <h2 className="text-sm font-semibold text-blue-500 p-0 m-0">
              {savingStatus === "saving" && "Saving..."}
              {savingStatus === "saved" && "Saved"}
              {savingStatus === "error" && "Failed to save"}
            </h2>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
